import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Lock, Plus, Trash2 } from 'lucide-react-native';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { toNumber } from './macros';
import type { MealFormData, NutritionPer100g } from './types';

interface ServingUnitsSectionProps {
  control: Control<MealFormData>;
  nutrition: NutritionPer100g;
}

const ServingUnitRow = ({
  control,
  index,
  nutrition,
  onRemove,
}: {
  control: Control<MealFormData>;
  index: number;
  nutrition: NutritionPer100g;
  onRemove: () => void;
}) => {
  const { t } = useTranslation('meal');
  const weight = toNumber(
    useWatch({ control, name: `servingUnits.${index}.weightGrams` }),
  );
  const factor = weight > 0 ? weight / 100 : 0;
  const summary = t('createMeal.servingUnits.summary', {
    kcal: Math.round(nutrition.energyKcal * factor),
    protein: Math.round(nutrition.protein * factor),
    carbs: Math.round(nutrition.carbs * factor),
    fats: Math.round(nutrition.fats * factor),
  });

  return (
    <View className="gap-2 rounded-2xl bg-surface p-3">
      <View className="flex-row items-center gap-2">
        <Controller
          control={control}
          name={`servingUnits.${index}.name`}
          rules={{ required: true, validate: (v) => v.trim().length > 0 }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder={t('createMeal.servingUnits.namePlaceholder')}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              className="h-12 flex-1 rounded-xl border-0 bg-background px-4"
            />
          )}
        />
        <Text className="text-text-muted">=</Text>
        <View className="w-20">
          <Controller
            control={control}
            name={`servingUnits.${index}.weightGrams`}
            rules={{ required: true, min: 0.1 }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                keyboardType="numeric"
                placeholder={t('createMeal.servingUnits.weightPlaceholder')}
                value={value ? String(value) : ''}
                onChangeText={(text) => onChange(toNumber(text))}
                onBlur={onBlur}
                className="h-12 rounded-xl border-0 bg-background px-4"
              />
            )}
          />
        </View>
        <Text className="text-sm text-text-muted">g</Text>
        <Button
          variant="surface"
          size="icon"
          onPress={onRemove}
          accessibilityLabel="remove"
          className="h-12 w-12 rounded-xl bg-background"
        >
          <Icon as={Trash2} size={18} className="text-text-muted" />
        </Button>
      </View>
      <View className="flex-row items-center gap-1.5">
        <Text className="text-xs text-text-muted">Σ</Text>
        <Text className="text-xs text-text-muted">{summary}</Text>
      </View>
    </View>
  );
};

/**
 * "Serving units" section: a locked 100 g base row plus a dynamic list of named
 * gram-based units (additive to the base), each with a live macro summary.
 */
export const ServingUnitsSection = ({
  control,
  nutrition,
}: ServingUnitsSectionProps) => {
  const { t } = useTranslation('meal');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'servingUnits',
  });

  return (
    <View className="gap-3">
      <View className="flex-row items-center justify-between">
        <Text className="text-sm font-sora-semibold text-text-primary">
          {t('createMeal.servingUnits.title')}
        </Text>
        <Text className="text-xs text-text-muted">
          {t('createMeal.servingUnits.count', { count: fields.length + 1 })}
        </Text>
      </View>
      <Text className="text-xs text-text-muted">
        {t('createMeal.servingUnits.subtitle')}
      </Text>

      {/* Base unit (locked) */}
      <View className="flex-row items-center gap-3 rounded-2xl border border-border bg-surface p-3">
        <View className="h-9 w-9 items-center justify-center rounded-full bg-background">
          <Icon as={Lock} size={16} className="text-text-muted" />
        </View>
        <View>
          <Text className="font-sora-semibold text-text-primary">100 g</Text>
          <Text className="text-xs text-text-muted">
            {t('createMeal.servingUnits.baseUnit')}
          </Text>
        </View>
      </View>

      {fields.map((field, index) => (
        <ServingUnitRow
          key={field.id}
          control={control}
          index={index}
          nutrition={nutrition}
          onRemove={() => remove(index)}
        />
      ))}

      <Button
        variant="ghost"
        onPress={() => append({ name: '', weightGrams: 0 })}
        className="flex-row gap-2"
      >
        <Icon as={Plus} size={18} className="text-text-secondary" />
        <Text className="text-sm font-medium text-text-secondary">
          {t('createMeal.servingUnits.addUnit')}
        </Text>
      </Button>
    </View>
  );
};
